import { useEffect, useState, useCallback, useRef } from 'react'

// Optimized scroll hook with throttling and RAF optimization
export const useOptimizedScroll = () => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [scrollProgress, setScrollProgress] = useState(0)
  const rafId = useRef<number>()
  const lastScrollY = useRef(0)
  const ticking = useRef(false)

  const updateScrollState = useCallback(() => {
    const scrollY = window.scrollY
    const windowHeight = window.innerHeight
    const documentHeight = document.documentElement.scrollHeight
    const scrollBottom = documentHeight - (scrollY + windowHeight)
    
    // Only update if scroll position actually changed (prevents unnecessary renders)
    if (Math.abs(scrollY - lastScrollY.current) < 1) {
      ticking.current = false
      return
    }
    
    lastScrollY.current = scrollY
    
    // Update navbar state (50px threshold)
    const newIsScrolled = scrollY > 50
    if (newIsScrolled !== isScrolled) {
      setIsScrolled(newIsScrolled)
    }
    
    // Calculate footer scroll progress for blur effects
    const blurStartDistance = 200
    const blurEndDistance = 50
    
    let progress = 0
    if (scrollBottom <= blurStartDistance && scrollBottom >= blurEndDistance) {
      progress = 1 - (scrollBottom - blurEndDistance) / (blurStartDistance - blurEndDistance)
    } else if (scrollBottom < blurEndDistance) {
      progress = 1
    }
    
    // Only update progress if it changed significantly (prevents micro-updates)
    if (Math.abs(progress - scrollProgress) > 0.01) {
      setScrollProgress(progress)
    }
    
    ticking.current = false
  }, [isScrolled, scrollProgress])

  const handleScroll = useCallback(() => {
    if (!ticking.current) {
      // Use RAF for smooth 60fps updates
      rafId.current = requestAnimationFrame(updateScrollState)
      ticking.current = true
    }
  }, [updateScrollState])

  useEffect(() => {
    // Use passive listener for better performance
    window.addEventListener('scroll', handleScroll, { passive: true })
    
    // Initial state
    updateScrollState()
    
    return () => {
      window.removeEventListener('scroll', handleScroll)
      if (rafId.current) {
        cancelAnimationFrame(rafId.current)
      }
    }
  }, [handleScroll, updateScrollState])

  return { isScrolled, scrollProgress }
}

// Specialized hook for pages that only need navbar state
export const useNavbarScroll = () => {
  const [isScrolled, setIsScrolled] = useState(false)
  const rafId = useRef<number>()
  const lastScrollY = useRef(0)
  const ticking = useRef(false)

  const updateNavbarState = useCallback(() => {
    const scrollY = window.scrollY
    
    // Only update if scroll position changed significantly
    if (Math.abs(scrollY - lastScrollY.current) < 5) {
      ticking.current = false
      return
    }
    
    lastScrollY.current = scrollY
    const newIsScrolled = scrollY > 50
    
    if (newIsScrolled !== isScrolled) {
      setIsScrolled(newIsScrolled)
    }
    
    ticking.current = false
  }, [isScrolled])

  const handleScroll = useCallback(() => {
    if (!ticking.current) {
      rafId.current = requestAnimationFrame(updateNavbarState)
      ticking.current = true
    }
  }, [updateNavbarState])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    updateNavbarState()
    
    return () => {
      window.removeEventListener('scroll', handleScroll)
      if (rafId.current) {
        cancelAnimationFrame(rafId.current)
      }
    }
  }, [handleScroll, updateNavbarState])

  return isScrolled
}

// Hook for complex scroll interactions (like sidebars)
export const useComplexScroll = () => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [scrollProgress, setScrollProgress] = useState(0)
  const [sidebarCollapseProgress, setSidebarCollapseProgress] = useState(0)
  const rafId = useRef<number>()
  const lastScrollY = useRef(0)
  const ticking = useRef(false)

  const updateComplexScrollState = useCallback(() => {
    const scrollY = window.scrollY
    const windowHeight = window.innerHeight
    const documentHeight = document.documentElement.scrollHeight
    
    // Prevent unnecessary updates
    if (Math.abs(scrollY - lastScrollY.current) < 1) {
      ticking.current = false
      return
    }
    
    lastScrollY.current = scrollY
    
    // Navbar state
    const newIsScrolled = scrollY > 50
    if (newIsScrolled !== isScrolled) {
      setIsScrolled(newIsScrolled)
    }
    
    // Footer progress
    const scrollBottom = documentHeight - (scrollY + windowHeight)
    const blurStartDistance = 200
    const blurEndDistance = 50
    
    let progress = 0
    if (scrollBottom <= blurStartDistance && scrollBottom >= blurEndDistance) {
      progress = 1 - (scrollBottom - blurEndDistance) / (blurStartDistance - blurEndDistance)
    } else if (scrollBottom < blurEndDistance) {
      progress = 1
    }
    
    if (Math.abs(progress - scrollProgress) > 0.01) {
      setScrollProgress(progress)
    }
    
    // Sidebar collapse progress (for documentation/API pages)
    const sidebarCollapseStart = 100
    const sidebarCollapseEnd = 300
    let collapseProgress = 0
    
    if (scrollY >= sidebarCollapseStart && scrollY <= sidebarCollapseEnd) {
      collapseProgress = (scrollY - sidebarCollapseStart) / (sidebarCollapseEnd - sidebarCollapseStart)
    } else if (scrollY > sidebarCollapseEnd) {
      collapseProgress = 1
    }
    
    if (Math.abs(collapseProgress - sidebarCollapseProgress) > 0.01) {
      setSidebarCollapseProgress(collapseProgress)
    }
    
    ticking.current = false
  }, [isScrolled, scrollProgress, sidebarCollapseProgress])

  const handleScroll = useCallback(() => {
    if (!ticking.current) {
      rafId.current = requestAnimationFrame(updateComplexScrollState)
      ticking.current = true
    }
  }, [updateComplexScrollState])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    updateComplexScrollState()
    
    return () => {
      window.removeEventListener('scroll', handleScroll)
      if (rafId.current) {
        cancelAnimationFrame(rafId.current)
      }
    }
  }, [handleScroll, updateComplexScrollState])

  return { isScrolled, scrollProgress, sidebarCollapseProgress }
}